import { useEffect, useMemo, useState } from 'react';
import { TableDefinition } from '~/Components/DataTable/DataTable.types';
import { IFitoList } from '~/Components/FitoList/FitoListUncontrolled/interfaces';
import { currencyToSymbol } from '~/Constants/Currency.constants';
import { centsToEuros } from '~/Helpers/Currency.helpers';
import { useDateFns, useLanguage } from '~/Hooks';
import { IPayouts } from '~/Models/DTO/Payouts.model';
import { useRequestPayoutsInvoice } from '~/requests/uscProxyService/useRequestPayoutsList';
import { usePayoutsColumns } from './usePayoutsColumns';

export const usePayouts = () => {
  const { columnMap } = usePayoutsColumns();
  const { formatFromDate } = useDateFns();
  const { t } = useLanguage();
  const [invoiceId, setInvoiceId] = useState(0);
  const [buttonClicked, setButtonClicked] = useState(false);
  const {
    downloadInvoicesFetching,
    downloadInvoicesLoading,
    downloadInvoicesData,
    refetchDownloadInvoices,
  } = useRequestPayoutsInvoice(invoiceId);

  useEffect(() => {
    if (invoiceId === 0) return;
    refetchDownloadInvoices();
  }, [invoiceId]);

  useEffect(() => {
    if (downloadInvoicesLoading || downloadInvoicesFetching) return;
    if (downloadInvoicesData?.url && buttonClicked) {
      window.open(downloadInvoicesData?.url, '_blank', 'noopener, noreferrer');
      setButtonClicked(false);
    }
  }, [downloadInvoicesFetching, downloadInvoicesLoading, buttonClicked]);

  const keyOfId: keyof IPayouts = 'id';

  const displayCurrency = (amount: string, currency: string) =>
    `${centsToEuros(amount)} ${currency}`;

  const handleDownloadClick = (id: number) => {
    setButtonClicked(true);
    setInvoiceId(id);
  };

  const createListItem = (item: IPayouts) => ({
    header: formatFromDate(item.service_period, 'MMMM yyyy'),
    subheader: displayCurrency(
      item.payout_amount_gross.amount.toString(),
      currencyToSymbol[item.payout_amount_gross.currency],
    ),
    status:
      item.status === 'cancelled'
        ? t('app.payouts.status.cancelled')
        : t('app.payouts.status.valid'),
    actionButton: item.status !== 'cancelled' && t('app.download'),
    actionButtonHandler: () => handleDownloadClick(item.id),
  });
  const setItemValues = (item: IPayouts): IFitoList => createListItem(item);

  const settings = {
    tabs: false,
    pagination: true,
  };

  const tableDefinition: TableDefinition = useMemo(
    () => ({
      columns: [
        columnMap.period,
        columnMap.uploadAt,
        columnMap.invoiceNumber,
        columnMap.status,
        columnMap.amount,
        columnMap.download,
      ],
      config: { useSelectableRows: false, usePagination: true },
    }),
    [columnMap],
  );

  return {
    keyOfId,
    setItemValues,
    settings,
    tableDefinition,
  };
};
